import styles from './OrderButtons.module.css'

export interface OrderButtonsProps {
  /** Names the thing being moved, for the buttons' accessible names. */
  label: string
  index: number
  total: number
  disabled?: boolean
  /** Receives the destination index. The caller sends a complete reordering. */
  onMove: (to: number) => void
}

/**
 * Move-up / move-down, rather than drag-and-drop.
 *
 * Two reasons, and the first is the API's. The ordering endpoints want a *complete*
 * list of ids and reject a partial one; a pair of buttons that each produce one whole
 * reordered array fit that exactly, where a drag interaction would have to reconstruct
 * the same array anyway with a lot more machinery in between.
 *
 * The second is the target: this is a 390px phone at a table. Drag needs pointer
 * precision and a scroll container that knows not to scroll; two 36px buttons need
 * neither, and they work from the keyboard for free.
 */
export function OrderButtons({ label, index, total, disabled = false, onMove }: OrderButtonsProps) {
  return (
    <span className={styles.orderButtons}>
      <button
        type="button"
        className={styles.orderButton}
        aria-label={`Move ${label} up`}
        disabled={disabled || index === 0}
        onClick={() => onMove(index - 1)}
      >
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <path
            d="M4 10l4-4 4 4"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className={styles.orderButton}
        aria-label={`Move ${label} down`}
        disabled={disabled || index >= total - 1}
        onClick={() => onMove(index + 1)}
      >
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </span>
  )
}
